import React, { useCallback, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0TimelineRow, TwoColumnLayout } from '@factorialco/f0-react'
import type { F0TimelineRowAction, F0TimelineRowTaskProps } from '@factorialco/f0-react'
import {
  ArrowCycle,
  CardPin,
  Check,
  CheckCircleLine,
  Cross,
  Download,
  MessageFilled,
  Pencil,
  Upload,
} from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  CompleteFormTaskStatusChangeDocument,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
  PurchaseRequestUpdatedSubscriptionDocument,
  TimelineItemEmployeesDocument,
  UpdateTaskStatusesDocument,
  VendorOnboardingTaskStatusChangeDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { stringToDate, i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import RejectionReasonModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RejectionReasonModal'
import DownloadDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/DownloadDocumentModal'
import RequestDocumentModal from 'modules/procurement/components/purchaseRequests/PurchaseRequestDetail/RequestDocumentModal'
import RequestChangesModal from 'modules/procurement/components/purchaseRequests/RequestChangesModal'
import PurchaseRequestDetails from 'modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestDetails'
import PurchaseRequestSummarySidebar from 'modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestSummarySidebar'
import TimelineApprovalActions from 'modules/procurement/pages/PurchaseRequestDetailPage/TimelineApprovalActions'
import TimelineDocumentTaskActions from 'modules/procurement/pages/PurchaseRequestDetailPage/TimelineDocumentTaskActions'
import TimelineFormActions from 'modules/procurement/pages/PurchaseRequestDetailPage/TimelineFormActions'
import TimelineTaskCompleteActions from 'modules/procurement/pages/PurchaseRequestDetailPage/TimelineTaskCompleteActions'

import MultiTaskGroup from './MultiTaskGroup'
import type { ApprovalsV2ApprovalTimelineEntry, EntryActionInfo } from './workflowApprovalTypes'
import {
  calculateDaysOverdue,
  groupTimelineEntries,
  mapTimelineStatus,
  getEntryIcon,
  formatEntryDescription,
  buildMetadata,
} from './workflowApprovalHelpers'

interface Props {
  purchaseRequest: PurchaseRequestFragment
  timelineEntries: ApprovalsV2ApprovalTimelineEntry[]
}

const WorkflowApprovalLayout = ({ purchaseRequest, timelineEntries }: Props) => {
  const { employee, access, company } = useCurrentSession()
  const approvalProcess = purchaseRequest?.approvalProcess
  const [locallyCompletedFormExecutionIds, setLocallyCompletedFormExecutionIds] = useState(
    () => new Set<string>()
  )
  const markFormExecutionCompleted = useCallback((handlerExecutionId: string) => {
    setLocallyCompletedFormExecutionIds((prev) => new Set(prev).add(handlerExecutionId))
  }, [])
  const reviewRequests = useMemo(
    () => approvalProcess?.reviewRequests ?? [],
    [approvalProcess?.reviewRequests]
  )

  const pendingReviewRequestIdsByHandlerExecution = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const rr of reviewRequests) {
      if (rr.decision) continue
      const heId = rr.workflowsHandlerExecutionId
      if (heId == null) continue
      const existing = map.get(heId) ?? []
      existing.push(rr.id)
      map.set(heId, existing)
    }
    return map
  }, [reviewRequests])

  const documentTasksByHandlerExecutionId = useMemo(() => {
    const map = new Map<string, PurchaseRequestFragment['uploadDocumentTask'][number]>()
    for (const task of purchaseRequest?.uploadDocumentTask ?? []) {
      if (task.taskStatus !== 'todo') continue
      const heId = task.workflowsHandlerExecutionId
      if (heId) map.set(heId, task)
    }
    return map
  }, [purchaseRequest?.uploadDocumentTask])

  type FormTaskInfo = { formId: string; materializedFormId: string; taskAssigneeIds: string[] }

  const formTasksByHandlerExecutionId = useMemo(() => {
    const map = new Map<string, FormTaskInfo>()
    for (const task of purchaseRequest?.completeFormTask ?? []) {
      if (task.taskStatus === 'done') continue
      const heId = task.workflowsHandlerExecutionId
      if (heId && task.completeFormFormId && task.formsMaterializedFormId) {
        if (locallyCompletedFormExecutionIds.has(heId)) continue
        map.set(heId, {
          formId: task.completeFormFormId,
          materializedFormId: task.formsMaterializedFormId,
          taskAssigneeIds: task.taskAssigneeIds ?? [],
        })
      }
    }
    for (const task of purchaseRequest?.vendorOnboardingTask ?? []) {
      if (task.taskStatus === 'done') continue
      const heId = task.workflowsHandlerExecutionId
      if (heId && task.vendorOnboardingFormId && task.formsMaterializedFormId) {
        if (locallyCompletedFormExecutionIds.has(heId)) continue
        map.set(heId, {
          formId: task.vendorOnboardingFormId,
          materializedFormId: task.formsMaterializedFormId,
          taskAssigneeIds: task.taskAssigneeIds ?? [],
        })
      }
    }
    return map
  }, [
    purchaseRequest?.completeFormTask,
    purchaseRequest?.vendorOnboardingTask,
    locallyCompletedFormExecutionIds,
  ])

  const completedFormHandlerExecutionIds = useMemo(() => {
    const ids = new Set<string>()
    for (const task of purchaseRequest?.completeFormTask ?? []) {
      if (task.taskStatus === 'done' && task.workflowsHandlerExecutionId) {
        ids.add(task.workflowsHandlerExecutionId)
      }
    }
    for (const task of purchaseRequest?.vendorOnboardingTask ?? []) {
      if (task.taskStatus === 'done' && task.workflowsHandlerExecutionId) {
        ids.add(task.workflowsHandlerExecutionId)
      }
    }
    return ids
  }, [purchaseRequest?.completeFormTask, purchaseRequest?.vendorOnboardingTask])

  const taskAssigneeIdsByTaskId = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const task of purchaseRequest?.task ?? []) {
      if (!task?.id) continue
      map.set(task.id, task.taskAssigneeIds ?? [])
    }
    return map
  }, [purchaseRequest?.task])

  const getEnrichedAssignees = useCallback(
    (entry: ApprovalsV2ApprovalTimelineEntry) => {
      const baseAssignees = entry.assignees ?? []
      if (entry.actionGroup !== 'task') return baseAssignees
      if (!entry.taskId) return baseAssignees

      const fallbackIds = taskAssigneeIdsByTaskId.get(entry.taskId) ?? []
      if (fallbackIds.length === 0) return baseAssignees

      const existingIds = new Set(
        baseAssignees.map((a) => a.employeeId).filter((id): id is string => id != null)
      )

      const fallbackAssignees = fallbackIds
        .filter((id) => !existingIds.has(id))
        .map((id) => ({
          employeeId: id,
          peopleGroupName: null,
          completed: false,
        }))

      return [...baseAssignees, ...fallbackAssignees]
    },
    [taskAssigneeIdsByTaskId]
  )

  const employeeIds = useMemo(
    () => [
      ...new Set(
        timelineEntries.flatMap((entry) =>
          getEnrichedAssignees(entry)
            .map((a) => a.employeeId)
            .filter((id): id is string => id != null)
        )
      ),
    ],
    [timelineEntries, getEnrichedAssignees]
  )

  const { data: employeesData } = useQuery(TimelineItemEmployeesDocument, {
    variables: { ids: employeeIds },
    skip: employeeIds.length === 0,
  })

  const employees = employeesData?.employees?.employeeNamesConnection?.nodes || []

  const apolloClient = useApolloClient()

  useSubscription(PurchaseRequestUpdatedSubscriptionDocument, {
    variables: { id: purchaseRequest.id },
    onData: () => {
      apolloClient.refetchQueries({ include: [PurchaseRequestDetailDocument] })
    },
  })

  useSubscription(CompleteFormTaskStatusChangeDocument, {
    onData: () => {
      apolloClient.refetchQueries({ include: [PurchaseRequestDetailDocument] })
    },
  })

  useSubscription(VendorOnboardingTaskStatusChangeDocument, {
    onData: () => {
      apolloClient.refetchQueries({ include: [PurchaseRequestDetailDocument] })
    },
  })

  // --- Multitask entry actions ---
  // Mutations needed for multitask items (items are data objects, not React elements,
  // so we can't use the render-prop action components for them).
  const [bulkApproveReviewRequest, { loading: multitaskApproving }] = useMutation(
    BulkApproveApprovalsv2ReviewRequestDocument,
    {
      extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const [updateTaskStatuses, { loading: multitaskCompleting }] = useMutation(
    UpdateTaskStatusesDocument,
    {
      extractorFn: (data) => data.tasksMutations.bulkMarkAsTasksInboxTask,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  type MultitaskModalState =
    | { type: 'requestChanges'; reviewRequestIds: string[] }
    | { type: 'rejection'; reviewRequestIds: string[] }
    | { type: 'upload'; documentTaskId: string }
    | { type: 'download'; documentTaskId: string }
    | { type: 'formTask'; formId: string; materializedFormId: string; handlerExecutionId?: string }

  const [multitaskModal, setMultitaskModal] = useState<MultitaskModalState | null>(null)
  const [completedMultitaskKeys, setCompletedMultitaskKeys] = useState<Set<string>>(new Set())

  const markMultitaskCompleted = useCallback((key: string) => {
    setCompletedMultitaskKeys((prev) => new Set(prev).add(key))
  }, [])

  const buildMultitaskEntryActions = useCallback(
    (
      entry: ApprovalsV2ApprovalTimelineEntry,
      actionInfo: EntryActionInfo | undefined
    ): Pick<F0TimelineRowTaskProps, 'primaryAction' | 'secondaryActions'> => {
      if (!actionInfo) return {}

      const entryKey = entry.handlerExecutionId ?? entry.taskId ?? ''
      const isDone = completedMultitaskKeys.has(entryKey)

      switch (actionInfo.type) {
        case 'approval':
          return {
            primaryAction: {
              label: i18n.t('procurement.purchase_requests.detail.actions.approve'),
              icon: Check,
              onClick: async () => {
                const result = await bulkApproveReviewRequest({
                  variables: {
                    reviewRequestIds: actionInfo.reviewRequestIds,
                    companyId: company.id,
                    reviewerId: employee.id,
                  },
                })
                const handled = handleResult(result, () => {
                  app.setFlash({
                    type: 'wrong',
                    message: i18n.t('procurement.purchase_requests.detail.responses.approve.error'),
                  })
                })
                if (handled.success) {
                  markMultitaskCompleted(entryKey)
                  const hasRemainingSteps =
                    timelineEntries.filter(
                      (e) =>
                        (e.status === 'running' || e.status === 'changesrequested') &&
                        (e.handlerExecutionId ?? e.taskId) !== entryKey
                    ).length > 0
                  app.setFlash({
                    type: 'correct',
                    message: hasRemainingSteps
                      ? i18n.t(
                          'procurement.purchase_requests.detail.responses.approve.success_pending'
                        )
                      : i18n.t(
                          'procurement.purchase_requests.detail.responses.approve.success_approved'
                        ),
                  })
                }
              },
              disabled: multitaskApproving || isDone,
              loading: multitaskApproving,
            },
            secondaryActions: [
              {
                label: i18n.t('procurement.purchase_requests.detail.actions.changes_requested'),
                icon: MessageFilled,
                onClick: () =>
                  setMultitaskModal({
                    type: 'requestChanges',
                    reviewRequestIds: actionInfo.reviewRequestIds,
                  }),
                disabled: multitaskApproving || isDone,
              },
              {
                label: i18n.t('procurement.purchase_requests.detail.actions.reject'),
                icon: Cross,
                onClick: () =>
                  setMultitaskModal({
                    type: 'rejection',
                    reviewRequestIds: actionInfo.reviewRequestIds,
                  }),
                disabled: multitaskApproving || isDone,
              },
            ],
          }
        case 'taskComplete':
          return {
            primaryAction: {
              label: i18n.t('tasks.mark.done'),
              icon: CheckCircleLine,
              onClick: async () => {
                const result = await updateTaskStatuses({
                  variables: { taskIds: [actionInfo.taskId], status: 'done' },
                })
                const handled = handleResult(result, () => {
                  app.setFlash({
                    type: 'wrong',
                    message: i18n.t('tasks.responses.mark_as_done_failed'),
                  })
                })
                if (handled.success) {
                  markMultitaskCompleted(entryKey)
                  app.setFlash({
                    type: 'correct',
                    message: i18n.t('tasks.responses.mark_as_done'),
                  })
                }
              },
              disabled: multitaskCompleting || isDone,
              loading: multitaskCompleting,
            },
          }
        case 'documentTask': {
          const isUpload = actionInfo.documentTask.taskType === 'upload'
          return {
            primaryAction: isUpload
              ? {
                  label: i18n.t(
                    'procurement.purchase_requests.detail.actions.upload_document_task'
                  ),
                  icon: Upload,
                  onClick: () =>
                    setMultitaskModal({
                      type: 'upload',
                      documentTaskId: actionInfo.documentTask.id,
                    }),
                }
              : {
                  label: i18n.t(
                    'procurement.purchase_requests.detail.actions.download_document_task'
                  ),
                  icon: Download,
                  onClick: () =>
                    setMultitaskModal({
                      type: 'download',
                      documentTaskId: actionInfo.documentTask.id,
                    }),
                },
          }
        }
        case 'formTask':
          return {
            primaryAction: {
              label: i18n.t('procurement.purchase_requests.detail.actions.complete_form'),
              icon: Pencil,
              onClick: () =>
                setMultitaskModal({
                  type: 'formTask',
                  formId: actionInfo.formId,
                  materializedFormId: actionInfo.materializedFormId,
                  handlerExecutionId: entry.handlerExecutionId ?? undefined,
                }),
              disabled: isDone,
            },
          }
      }
    },
    [
      completedMultitaskKeys,
      bulkApproveReviewRequest,
      updateTaskStatuses,
      multitaskApproving,
      multitaskCompleting,
      company.id,
      employee.id,
      markMultitaskCompleted,
      timelineEntries,
    ]
  )

  const getEntryActionInfo = (
    entry: ApprovalsV2ApprovalTimelineEntry
  ): EntryActionInfo | undefined => {
    if (entry.status !== 'running' && entry.status !== 'changesrequested') return undefined

    const enrichedAssignees = getEnrichedAssignees(entry)
    const isEntryAssignee = enrichedAssignees.some(
      (a) => a.employeeId != null && String(a.employeeId) === String(employee.id)
    )

    // Task actions: available when user is a direct assignee
    if (entry.actionGroup === 'task') {
      if (entry.handlerExecutionId) {
        const docTask = documentTasksByHandlerExecutionId.get(entry.handlerExecutionId)
        if (docTask && docTask.taskAssigneeIds?.includes(access.id)) {
          return { type: 'documentTask', documentTask: docTask }
        }

        const formTask = formTasksByHandlerExecutionId.get(entry.handlerExecutionId)
        if (formTask && formTask.taskAssigneeIds.includes(access.id)) {
          return {
            type: 'formTask',
            formId: formTask.formId,
            materializedFormId: formTask.materializedFormId,
            taskAssigneeIds: formTask.taskAssigneeIds,
          }
        }

        // Form handler already completed — don't fall through to taskComplete
        if (entry.handlerType === 'complete_form' || entry.handlerType === 'vendor_onboarding') {
          return undefined
        }
      }
      // "Complete form" tasks are completed by submitting the form through the
      // inbox/forms UI, not by clicking "Mark as Done" in the timeline.
      if (entry.handlerType === 'complete_form') {
        return undefined
      }
      if (entry.taskId && isEntryAssignee) {
        return { type: 'taskComplete', taskId: entry.taskId }
      }
      return undefined
    }

    // Approval actions: only when request is still pending and the current
    // user is an assignee with a pending review request for this handler execution.
    if (purchaseRequest.status !== 'pending') return undefined
    if (!isEntryAssignee) return undefined

    if (entry.handlerExecutionId) {
      const entryReviewRequestIds =
        pendingReviewRequestIdsByHandlerExecution.get(entry.handlerExecutionId) ?? []
      if (entryReviewRequestIds.length > 0) {
        return { type: 'approval', reviewRequestIds: entryReviewRequestIds }
      }
    }

    return undefined
  }

  const getEffectiveEntryStatus = (entry: ApprovalsV2ApprovalTimelineEntry): string => {
    if (
      entry.status === 'running' &&
      entry.handlerExecutionId &&
      (completedFormHandlerExecutionIds.has(entry.handlerExecutionId) ||
        locallyCompletedFormExecutionIds.has(entry.handlerExecutionId))
    ) {
      return 'completed'
    }
    return entry.status
  }

  const visibleEntries = useMemo(
    () => timelineEntries.filter((entry) => entry.type !== 'start'),
    [timelineEntries]
  )

  const groups = useMemo(() => groupTimelineEntries(visibleEntries), [visibleEntries])

  const formatAlertDate = (timestamp: string) => {
    const date = stringToDate(timestamp, 'backend_datetime')
    return i18nDateToString(date, 'ui_short_with_time')
  }

  const isAutoRejected =
    approvalProcess?.decision === 'rejected' &&
    !approvalProcess?.decisionReason &&
    !reviewRequests.some((reviewRequest) => reviewRequest.decision === 'rejected')

  const automaticRejectionAlert =
    isAutoRejected && approvalProcess?.updatedAt ? (
      <F0Alert
        variant="critical"
        title={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.title'
        )}
        description={i18n.t(
          'procurement.purchase_requests.detail.timeline.automatic_rejection_alert.description',
          {
            date: formatAlertDate(approvalProcess.updatedAt),
          }
        )}
        icon={CardPin}
      />
    ) : null

  const isCurrentUserRequester = approvalProcess?.requesterId === employee.id

  const getDaysOverdue = (entry: ApprovalsV2ApprovalTimelineEntry): number | undefined => {
    if (getEffectiveEntryStatus(entry) === 'running' && entry.dueOn) {
      return calculateDaysOverdue(entry.dueOn)
    }
    return undefined
  }

  const getAlertForEntry = (
    entry: ApprovalsV2ApprovalTimelineEntry,
    daysOverdue: number | undefined
  ) => {
    const { status, decisionReason, time: timestamp } = entry

    if (daysOverdue !== undefined && daysOverdue > 0) {
      const isEntryAssignee =
        !isCurrentUserRequester && (entry.assignees ?? []).some((a) => a.employeeId === employee.id)

      const alertTitle = isEntryAssignee
        ? i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.assignee.title', {
            count: daysOverdue,
          })
        : i18n.tp('procurement.purchase_requests.detail.timeline.overdue_alert.title', {
            count: daysOverdue,
          })

      return <F0Alert variant="warning" title={alertTitle} description="" />
    }

    const isRejected = status === 'rejected'
    const isChangesRequested = status === 'changesrequested'

    if ((isRejected || isChangesRequested) && decisionReason) {
      const decision = isRejected ? 'rejected' : 'changes_requested'

      const matchingReviewRequest = reviewRequests.find(
        (reviewRequest) =>
          reviewRequest.decisionReason === decisionReason && reviewRequest.decision === decision
      )

      const reviewerTime = matchingReviewRequest?.reviewedAt || timestamp

      let reviewerName = ''
      if (entry.assignees && entry.assignees.length > 0) {
        const firstAssignee = entry.assignees[0]
        if (firstAssignee?.peopleGroupName) {
          reviewerName = firstAssignee?.peopleGroupName ?? ''
        } else if (firstAssignee?.employeeId) {
          const emp = employees.find((e) => e?.id === firstAssignee?.employeeId)
          reviewerName = emp?.fullName || ''
        }
      }

      return (
        <F0Alert
          variant={isRejected ? 'critical' : 'warning'}
          title={
            isRejected
              ? i18n.t('procurement.purchase_requests.detail.timeline.rejected_alert.title', {
                  reason: decisionReason,
                })
              : i18n.t(
                  'procurement.purchase_requests.detail.timeline.changes_requested_alert.title',
                  {
                    reason: decisionReason,
                  }
                )
          }
          description={`${reviewerName}, ${formatAlertDate(reviewerTime)}`}
          icon={CardPin}
        />
      )
    }

    return undefined
  }

  const renderWithActions = (
    actionInfo: ReturnType<typeof getEntryActionInfo>,
    entry: ApprovalsV2ApprovalTimelineEntry,
    key: React.Key,
    children: (props: {
      primaryAction?: F0TimelineRowAction
      secondaryActions?: F0TimelineRowAction[]
    }) => React.ReactNode
  ): React.ReactNode => {
    if (!actionInfo) return <React.Fragment key={key}>{children({})}</React.Fragment>

    switch (actionInfo.type) {
      case 'approval': {
        const entryKey = entry.handlerExecutionId ?? entry.taskId ?? ''
        return (
          <TimelineApprovalActions
            key={key}
            reviewRequestIds={actionInfo.reviewRequestIds}
            onApproveSuccess={() => {
              const hasRemainingSteps =
                timelineEntries.filter(
                  (e) =>
                    (e.status === 'running' || e.status === 'changesrequested') &&
                    (e.handlerExecutionId ?? e.taskId) !== entryKey
                ).length > 0
              app.setFlash({
                type: 'correct',
                message: hasRemainingSteps
                  ? i18n.t('procurement.purchase_requests.detail.responses.approve.success_pending')
                  : i18n.t(
                      'procurement.purchase_requests.detail.responses.approve.success_approved'
                    ),
              })
            }}
          >
            {children}
          </TimelineApprovalActions>
        )
      }
      case 'taskComplete':
        return (
          <TimelineTaskCompleteActions key={key} taskId={actionInfo.taskId}>
            {children}
          </TimelineTaskCompleteActions>
        )
      case 'documentTask':
        return (
          <TimelineDocumentTaskActions key={key} documentTask={actionInfo.documentTask}>
            {children}
          </TimelineDocumentTaskActions>
        )
      case 'formTask': {
        const heId = entry.handlerExecutionId
        return (
          <TimelineFormActions
            key={key}
            formId={actionInfo.formId}
            materializedFormId={actionInfo.materializedFormId}
            onFormCompleted={heId ? () => markFormExecutionCompleted(heId) : undefined}
          >
            {children}
          </TimelineFormActions>
        )
      }
    }
  }

  const mainContent = (
    <Box gap="s24">
      <PurchaseRequestDetails purchaseRequest={purchaseRequest} />

      <Box gap="s16">
        <Text.Normal size="300" weight="semibold" color="grey1000">
          {i18n.t('approvals_v2.timeline.request_status')}
        </Text.Normal>
        <Box className="max-w-[800px]">
          {automaticRejectionAlert}

          <>
            {groups.map((group, groupIdx) => {
              const isLast = groupIdx === groups.length - 1

              if (group.kind === 'multi') {
                const enrichedEntries = group.entries.map((entry) => ({
                  ...entry,
                  assignees: getEnrichedAssignees(entry),
                }))
                const changesRequestedEntry = group.entries.find(
                  (e) =>
                    (e.status === 'changesrequested' || e.status === 'rejected') && e.decisionReason
                )
                const groupAlert = changesRequestedEntry
                  ? getAlertForEntry(changesRequestedEntry, undefined)
                  : undefined

                return (
                  <MultiTaskGroup
                    key={`group-${group.index}`}
                    entries={enrichedEntries}
                    buildEntryItem={(entry, _idx, isEntryLast) => {
                      const effectiveStatus = getEffectiveEntryStatus(entry)
                      return {
                        status: mapTimelineStatus(effectiveStatus),
                        icon: getEntryIcon(entry.type, entry.actionGroup),
                        title: entry.title,
                        description: formatEntryDescription(entry.time, effectiveStatus),
                        metadata: buildMetadata(entry, employees),
                        hideStatus: true,
                        isLast: isEntryLast,
                        ...buildMultitaskEntryActions(entry, getEntryActionInfo(entry)),
                      }
                    }}
                    alert={groupAlert}
                    isLast={isLast}
                  />
                )
              }

              const entry = group.entry
              const enrichedEntry = {
                ...entry,
                assignees: getEnrichedAssignees(entry),
              }
              const daysOverdue = getDaysOverdue(entry)
              const alert = getAlertForEntry(entry, daysOverdue)
              const actionInfo = getEntryActionInfo(enrichedEntry)

              if (entry.type === 'cannot_resolve') {
                return (
                  <F0TimelineRow
                    key={`single-${group.index}`}
                    status="not-started"
                    icon={ArrowCycle}
                    title={i18n.t('approvals_v2.timeline.cannot_resolve.title')}
                    description={i18n.t('approvals_v2.timeline.cannot_resolve.description')}
                    isLast={isLast}
                  />
                )
              }

              return renderWithActions(
                actionInfo,
                enrichedEntry,
                `single-${group.index}`,
                ({ primaryAction, secondaryActions }) => (
                  <>
                    <F0TimelineRow
                      status={mapTimelineStatus(getEffectiveEntryStatus(entry))}
                      icon={getEntryIcon(entry.type, entry.actionGroup)}
                      title={enrichedEntry.title}
                      description={formatEntryDescription(
                        entry.time,
                        getEffectiveEntryStatus(entry)
                      )}
                      metadata={buildMetadata(enrichedEntry, employees)}
                      isLast={isLast}
                      primaryAction={primaryAction}
                      secondaryActions={secondaryActions}
                    />
                    {alert}
                  </>
                )
              )
            })}
          </>
        </Box>
      </Box>
    </Box>
  )

  const sideContent = <PurchaseRequestSummarySidebar purchaseRequest={purchaseRequest} />

  const multitaskModals = (
    <>
      {multitaskModal?.type === 'requestChanges' && (
        <RequestChangesModal
          isOpen
          onClose={() => setMultitaskModal(null)}
          reviewRequestIds={multitaskModal.reviewRequestIds}
          onSuccess={() => {
            setMultitaskModal(null)
          }}
        />
      )}
      {multitaskModal?.type === 'rejection' && (
        <RejectionReasonModal
          isOpen
          onClose={() => setMultitaskModal(null)}
          reviewRequestIds={multitaskModal.reviewRequestIds}
          onSuccess={() => {
            setMultitaskModal(null)
            app.setFlash({
              type: 'correct',
              message: i18n.t('procurement.purchase_requests.detail.responses.reject.success'),
            })
          }}
        />
      )}
      {multitaskModal?.type === 'upload' &&
        (() => {
          const task = (purchaseRequest?.uploadDocumentTask ?? []).find(
            (t) => t.id === multitaskModal.documentTaskId
          )
          return task ? (
            <RequestDocumentModal
              isOpen
              onClose={() => setMultitaskModal(null)}
              uploadDocumentTask={task}
            />
          ) : null
        })()}
      {multitaskModal?.type === 'download' &&
        (() => {
          const task = (purchaseRequest?.uploadDocumentTask ?? []).find(
            (t) => t.id === multitaskModal.documentTaskId
          )
          return task ? (
            <DownloadDocumentModal
              isOpen
              onClose={() => setMultitaskModal(null)}
              documentTask={task}
            />
          ) : null
        })()}
      {multitaskModal?.type === 'formTask' &&
        (() => {
          const heId = multitaskModal.handlerExecutionId
          return (
            <TimelineFormActions
              formId={multitaskModal.formId}
              materializedFormId={multitaskModal.materializedFormId}
              autoOpen
              onClose={() => setMultitaskModal(null)}
              onFormCompleted={heId ? () => markFormExecutionCompleted(heId) : undefined}
            >
              {() => null}
            </TimelineFormActions>
          )
        })()}
    </>
  )

  return (
    <>
      <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
      {multitaskModals}
    </>
  )
}

export default WorkflowApprovalLayout
